import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ChevronDown, ChevronRight, Calendar, X } from 'lucide-react';
import DarkModeToggle from './ui/dark-mode-toggle';
import { useDarkMode } from './DarkModeContext';
import ProfilePicture from './ProfilePicture';
import changelogData from '../data/changelog';

const UpdatesPage = ({ onClose, onSignIn, user, onShowPricing, onShowTerms, onShowPrivacy, onShowCookiePreferences, mockupCount, onLogout }) => {
  const { isDarkMode } = useDarkMode();
  const [expandedSections, setExpandedSections] = useState({});
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.dropdown-container')) {
        setShowUserDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);

  const toggleSection = (index) => {
    setExpandedSections(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="bg-surface-1/80 backdrop-blur-md flex-shrink-0 sticky top-0 z-50">
        <div className="w-full pl-6 pr-4 sm:pl-6 sm:pr-6 lg:pl-6 lg:pr-6">
          <div className="flex justify-between items-center py-3">
            <div className="flex items-center">
              <img 
                src={isDarkMode ? "/RandomMockups-Logo-LightMode.png" : "/RandomMockups-Logo.png"} 
                alt="RandomMockups Logo" 
                className="mr-1.5"
                style={{ height: '40px', width: 'auto' }}
              />
              <h1 
                className="text-xl font-semibold text-foreground cursor-pointer hover:text-muted-foreground transition-colors"
                onClick={onClose}
              >
                RandomMockups
              </h1>
            </div>
            <div className="flex items-center space-x-3">
              <DarkModeToggle />
              {!user && (
                <Button 
                  onClick={onSignIn}
                  variant="outline"
                  size="sm"
                  className="text-foreground hover:text-foreground bg-surface-2 hover:bg-muted"
                >
                  Sign in
                </Button>
              )}
              {user && (
                <button
                  onClick={() => {
                    onShowPricing();
                    window.history.pushState({}, '', '/pricing');
                  }}
                  className="flex items-center gap-2 text-sm font-medium text-foreground hover:bg-muted transition-colors p-2 rounded-lg"
                >
                  <ProfilePicture user={user} className="w-6 h-6 rounded-full" />
                  <span className={`bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center ${
                    mockupCount >= 10 
                      ? 'rounded-full px-2 py-1 min-w-8 h-6' 
                      : 'rounded-full w-5 h-5'
                  }`}>
                    {mockupCount}
                  </span>
                </button>
              )}
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1480px] mx-auto px-6 py-8 flex-1">
        <div className="grid grid-cols-5 gap-8 mb-8">
          {/* Left Column - Headline and Subheadline (2/5) */}
          <div className="col-span-2">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              What's new in RandomMockups
            </h1>
            <p className="text-sm text-muted-foreground">
              Track the latest features and improvements we've added to make your mockup creation experience even better.
            </p>
          </div>
          
          {/* Right Column - Updates Timeline (3/5) */}
          <div className="col-span-3">
            {/* Timeline */}
            <div className="space-y-4">
          {changelogData.map((update, index) => (
            <Card key={index} className="overflow-hidden">
              <CardContent className="p-6">
                <button
                  onClick={() => toggleSection(index)}
                  className="w-full text-left hover:bg-surface-1 transition-colors pt-6"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2 text-muted-foreground">
                        <Calendar className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{formatDate(update.date)}</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-foreground">
                          {update.title}
                        </h3>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-muted-foreground">
                        {update.changes.length} {update.changes.length === 1 ? 'update' : 'updates'}
                      </span>
                      {expandedSections[index] ? (
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                </button>
                
                {expandedSections[index] && (
                  <div className="border-t border-border mt-6">
                    <ul className="space-y-2 mt-4">
                      {update.changes.map((change, changeIndex) => (
                        <li key={changeIndex} className="flex items-start space-x-3">
                          <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                          <span className="text-sm text-foreground">{change}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
            </div>
          </div>
        </div>

      </div>

      {/* Footer */}
      <div className="bg-surface-1 py-8">
        <div className="max-w-[1480px] mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-muted-foreground space-y-2 md:space-y-0">
            <div>
              All rights reserved
            </div>
            <div className="flex space-x-4">
              <button 
                onClick={() => {
                  // Already on Updates page, no action needed
                }}
                className="hover:text-foreground transition-colors"
              >
                Updates
              </button>
              <button 
                onClick={() => {
                  onShowPricing();
                  window.history.pushState({}, '', '/pricing');
                }}
                className="hover:text-foreground transition-colors"
              >
                Pricing
              </button>
              <button 
                onClick={() => {
                  onShowTerms();
                  window.history.pushState({}, '', '/terms');
                }}
                className="hover:text-foreground transition-colors"
              >
                Terms
              </button>
              <button 
                onClick={() => {
                  onShowPrivacy();
                  window.history.pushState({}, '', '/privacy');
                }}
                className="hover:text-foreground transition-colors"
              >
                Privacy
              </button>
              <button 
                onClick={onShowCookiePreferences}
                className="hover:text-foreground transition-colors"
              >
                Cookie Settings
              </button>
            </div>
            <div>
              Made with 🍌 by <a href="https://x.com/txh" target="_blank" rel="noopener noreferrer" className="font-medium hover:text-foreground transition-colors">@txh</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdatesPage;
